/**
 * Asset Management · Lifecycle & capital plan.
 *
 * When each asset reaches the end of its design life, what replacing it
 * costs, and whether the capital envelopes cover the year it falls in. Assets
 * already past end of life are this year's money, not a year gone.
 *
 * Most registers do not carry a design life or replacement cost per asset,
 * so a policy per asset type ("an AGL light: 12 years, SAR 2,600") fills the
 * gap — and every figure that came from a policy is labelled so, here and on
 * the asset. The forecast only counts assets with an install date; the
 * coverage line says how many that is.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmLifecycle, useAmRegister, useAmSetPolicy } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, ErrorState, Input, Segmented, Skeleton, Table } from "../../design/components";
import type { AmLifecycle, AmPolicy } from "../../domain/assetManagement";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { EnvelopesCard } from "../entryForms/EnvelopesCard";
import { ColumnChart, Coverage, Criticality, errorText, money, PolicyTag, s, StageLabel, SUITE, Tiles } from "./shared";

type Tab = "forecast" | "assets" | "policies" | "warranties";
const TABS: { value: Tab; label: string }[] = [
  { value: "forecast", label: "Renewal forecast" },
  { value: "assets", label: "End of life" },
  { value: "policies", label: "Type policies" },
  { value: "warranties", label: "Warranties" },
];

export function AssetLifecyclePage() {
  const session = useSession();
  const q = useAmLifecycle();
  const [tab, setTab] = useState<Tab>("forecast");
  const l = q.data;
  const canEdit = hasPermission(session.principal, PERMISSION.assetWrite);

  useScreenContext(
    l
      ? {
          route: "/asset-lifecycle",
          title: "Lifecycle and capital plan",
          summary: [
            t("{n} assets are past their design life; renewing this year's backlog costs {cost}.", {
              n: l.totals.pastEndOfLife,
              cost: money(l.forecast[0]?.cost ?? 0),
            }),
            t("Replacement value on record: {v} across {n} assets; book value {b}.", {
              v: money(l.totals.replacementValue),
              n: l.totals.valuedAssets,
              b: money(l.totals.bookValue),
            }),
            t("{f} of {n} assets can be forecast (install date and design life known).", {
              f: l.coverage.forecastable,
              n: l.coverage.assets,
            }),
          ],
          offers: [
            { label: t("What needs renewing this year?"), ask: "which assets are past their design life?" },
            { label: t("Is the capital plan funded?"), ask: "does the capex envelope cover the renewal forecast?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Lifecycle and capital plan"
    >
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {l && (
        <>
          <Tiles
            items={[
              {
                key: "replacement",
                label: t("Replacement value"),
                value: money(l.totals.replacementValue),
                sub: t("{n} assets valued", { n: l.totals.valuedAssets }),
              },
              {
                key: "book",
                label: t("Book value"),
                value: money(l.totals.bookValue),
                sub: t("{n} assets with a cost, date and life", { n: l.totals.bookedAssets }),
              },
              {
                key: "eol",
                label: t("Past end of life"),
                value: l.totals.pastEndOfLife,
                tone: l.totals.pastEndOfLife ? "bad" : "good",
              },
              {
                key: "warranty",
                label: t("Warranties ending in 90 days"),
                value: l.warranties.expiring90.length,
                tone: l.warranties.expiring90.length ? "warn" : undefined,
                sub: t("{n} in warranty", { n: l.warranties.inWarranty }),
              },
            ]}
          />
          <Coverage
            lines={[
              { label: t("have an install or purchase date"), of: l.coverage.assets, n: l.coverage.dated },
              { label: t("have a design life (asset or type policy)"), of: l.coverage.assets, n: l.coverage.lifeKnown },
              {
                label: t("have a replacement value (asset or type policy)"),
                of: l.coverage.assets,
                n: l.coverage.valued,
              },
            ]}
          />
          <Segmented value={tab} options={TABS} onChange={setTab} ariaLabel="Lifecycle views" />
          {tab === "forecast" && (
            <>
              <Forecast l={l} />
              {/* build 6B: the envelopes the forecast is drawn against, entered here */}
              <EnvelopesCard />
            </>
          )}
          {tab === "assets" && <EndOfLife l={l} />}
          {tab === "policies" && <Policies l={l} canEdit={canEdit} />}
          {tab === "warranties" && <Warranties l={l} />}
          <p className={s.note}>
            {t("How it is worked out:")} {l.method}
          </p>
        </>
      )}
    </Page>
  );
}

function Forecast({ l }: { l: AmLifecycle }) {
  const anyFunded = l.forecast.some((f) => f.funded != null);
  const gap = l.forecast.filter((f) => f.funded != null && f.cost > (f.funded ?? 0));
  return (
    <Card
      title={t("Renewal cost by year")}
      sub={t("Replacement cost by end-of-life year. Backlog is in year one.")}
    >
      {l.coverage.forecastable === 0 ? (
        <EmptyState>
          {t(
            "Nothing can be forecast yet: no asset has both an install date and a design life. Import the dates, or set a type policy.",
          )}
        </EmptyState>
      ) : (
        <>
          <ColumnChart
            label={t("Renewal cost by year")}
            format={(v) => money(v).replace("SAR ", "")}
            marker={anyFunded}
            markerLabel={t("Capex envelope for the year")}
            data={l.forecast.map((f) => ({
              key: String(f.year),
              label: String(f.year),
              value: f.cost,
              marker: f.funded,
            }))}
          />
          <Table>
            <thead>
              <tr>
                <th>{t("Year")}</th>
                <th className={s.num}>{t("Assets")}</th>
                <th className={s.num}>{t("Of them critical")}</th>
                <th className={s.num}>{t("Renewal cost")}</th>
                <th className={s.num}>{t("Capex envelope")}</th>
                <th className={s.num}>{t("Not costed")}</th>
              </tr>
            </thead>
            <tbody>
              {l.forecast.map((f) => (
                <tr key={f.year}>
                  <td className={s.mono}>{f.year}</td>
                  <td className={s.num}>{f.count}</td>
                  <td className={s.num}>{f.critical}</td>
                  <td className={s.num}>{money(f.cost)}</td>
                  <td
                    className={s.num}
                    style={f.funded != null && f.cost > f.funded ? { color: "var(--red)" } : undefined}
                  >
                    {f.funded != null ? money(f.funded) : "—"}
                  </td>
                  <td className={s.num}>{f.uncosted || ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {gap.length > 0 && (
            <p className={s.err}>
              {t("The renewal cost exceeds the capex envelope in {years}.", {
                years: gap.map((g) => g.year).join(", "),
              })}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function EndOfLife({ l }: { l: AmLifecycle }) {
  if (!l.assets.length) return <EmptyState>{t("No asset has an end of life or a replacement value yet.")}</EmptyState>;
  return (
    <Card title={t("By end of life")} sub={t("Soonest first; past end of life at the top.")}>
      <Table>
        <thead>
          <tr>
            <th>{t("Tag")}</th>
            <th>{t("Type")}</th>
            <th>{t("Criticality")}</th>
            <th>{t("Stage")}</th>
            <th className={s.num}>{t("Age")}</th>
            <th className={s.num}>{t("Life")}</th>
            <th>{t("End of life")}</th>
            <th className={s.num}>{t("Replacement")}</th>
            <th className={s.num}>{t("Book value")}</th>
          </tr>
        </thead>
        <tbody>
          {l.assets.slice(0, 300).map((a) => (
            <tr key={a.id}>
              <td className={s.mono}>
                <Link to={`/asset-register/${encodeURIComponent(a.id)}`}>{a.tag}</Link>
              </td>
              <td>{a.assetTypeName}</td>
              <td>
                <Criticality value={a.criticality} />
              </td>
              <td>
                <StageLabel stage={a.stage} />
              </td>
              <td className={s.num}>{a.ageYears ?? "—"}</td>
              <td className={s.num}>
                {a.designLifeYears ?? "—"}
                <PolicyTag source={a.lifeSource} />
              </td>
              <td
                className={s.mono}
                style={a.remainingYears != null && a.remainingYears < 0 ? { color: "var(--red)" } : undefined}
              >
                {a.endOfLife ?? "—"}
              </td>
              <td className={s.num}>
                {money(a.replacementValue)}
                <PolicyTag source={a.valueSource} />
              </td>
              <td className={s.num}>{money(a.bookValue)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function Policies({ l, canEdit }: { l: AmLifecycle; canEdit: boolean }) {
  const reg = useAmRegister({});
  const set = useAmSetPolicy();
  const [draft, setDraft] = useState<Record<string, { life: string; cost: string }>>({});
  const byType = new Map(l.policies.map((p) => [p.assetType, p]));
  const types = reg.data?.types ?? [];
  const value = (key: string, p: AmPolicy | undefined) =>
    draft[key] ?? { life: p?.designLifeYears?.toString() ?? "", cost: p?.replacementCost?.toString() ?? "" };

  return (
    <Card
      title={t("Asset-type policies")}
      sub={t(
        "A design life and replacement cost per type, used wherever an asset has none of its own — and labeled 'policy' where used.",
      )}
    >
      {!types.length ? (
        <EmptyState>{t("No asset types on this site.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Type")}</th>
              <th className={s.num}>{t("Assets")}</th>
              <th>{t("Design life (years)")}</th>
              <th>{t("Replacement cost each (SAR)")}</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {types.map((ty) => {
              const p = byType.get(ty.key);
              const v = value(ty.key, p);
              return (
                <tr key={ty.key}>
                  <td>{ty.name}</td>
                  <td className={s.num}>{ty.assets}</td>
                  <td>
                    {canEdit ? (
                      <Input
                        inputMode="decimal"
                        value={v.life}
                        style={{ width: 90 }}
                        onChange={(e) => setDraft({ ...draft, [ty.key]: { ...v, life: e.target.value } })}
                        aria-label={t("Design life for {type}", { type: ty.name })}
                      />
                    ) : (
                      (p?.designLifeYears ?? "—")
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                      <Input
                        inputMode="decimal"
                        value={v.cost}
                        style={{ width: 130 }}
                        onChange={(e) => setDraft({ ...draft, [ty.key]: { ...v, cost: e.target.value } })}
                        aria-label={t("Replacement cost for {type}", { type: ty.name })}
                      />
                    ) : (
                      money(p?.replacementCost)
                    )}
                  </td>
                  {canEdit && (
                    <td>
                      {draft[ty.key] && (
                        <Button
                          size="sm"
                          disabled={set.isPending}
                          onClick={() =>
                            set.mutate(
                              {
                                assetType: ty.key,
                                policy: {
                                  designLifeYears: v.life.trim() ? Number(v.life) : null,
                                  replacementCost: v.cost.trim() ? Number(v.cost) : null,
                                  currency: "SAR",
                                  note: null,
                                },
                              },
                              {
                                onSuccess: () =>
                                  setDraft((d) => {
                                    const n = { ...d };
                                    delete n[ty.key];
                                    return n;
                                  }),
                              },
                            )
                          }
                          data-testid={`am-policy-save-${ty.key}`}
                        >
                          {t("Save")}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {set.error && <p className={s.err}>{errorText(set.error)}</p>}
    </Card>
  );
}

function Warranties({ l }: { l: AmLifecycle }) {
  return (
    <Card
      title={t("Warranties ending in 90 days")}
      sub={t("{a} in warranty · {b} expired", { a: l.warranties.inWarranty, b: l.warranties.expired })}
    >
      {l.warranties.expiring90.length ? (
        <Table>
          <thead>
            <tr>
              <th>{t("Tag")}</th>
              <th>{t("Name")}</th>
              <th>{t("Warranty until")}</th>
              <th className={s.num}>{t("Days left")}</th>
            </tr>
          </thead>
          <tbody>
            {l.warranties.expiring90.map((w) => (
              <tr key={w.id}>
                <td className={s.mono}>
                  <Link to={`/asset-register/${encodeURIComponent(w.id)}`}>{w.tag}</Link>
                </td>
                <td>{w.name}</td>
                <td className={s.mono}>{w.warrantyUntil}</td>
                <td className={s.num}>{w.daysLeft}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState>{t("No warranty ends in the next 90 days.")}</EmptyState>
      )}
    </Card>
  );
}
